import type { FC, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { Col, getTokenValue, Icons, Row, useMedia } from '@opendoor/bricks-next';
import { IconMessage, IconUser } from '@tabler/icons-react';

import { useAuthAuthenticated } from 'platform/auth-fe';
import { Link, TextButton } from 'platform/bricks-next';
import type { NavTabColor } from 'platform/components/layout/NavTab';
import { NavTab } from 'platform/components/layout/NavTab';
import { LogoLink } from 'platform/components/OpendoorLogo';

import { List } from 'shared/components/List';
import { ListItem } from 'shared/components/ListItem';
import { useBrowserCompatibilityData } from 'shared/helpers/globals';
import {
  ACCOUNT_URL,
  getHubContactRoute,
  getHubMoreRoute,
  getHubSellingRoute,
} from 'shared/helpers/routes';

// Do not change tab names. They are used in our metrics.
export const SELLING_TAB_NAME = 'sell';
export const MORE_TAB_NAME = 'more';
export const CONTACT_TAB_NAME = 'contact';
export const ACCOUNT_TAB_NAME = 'account';

type TabName =
  | typeof SELLING_TAB_NAME
  | typeof MORE_TAB_NAME
  | typeof CONTACT_TAB_NAME
  | typeof ACCOUNT_TAB_NAME;
export const ActiveTabNameContext = createContext<TabName>(SELLING_TAB_NAME);

/** Mobile bottom navigation bar with a More tab. */
export const MobilePrimaryNavBar: FC = () => {
  const activeTabName = useContext(ActiveTabNameContext);
  const tabs = useTabs({ showMobileMore: true });
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();

  return (
    <Col
      tag="nav"
      data-testid="mobile-primary-nav"
      borderTopWidth="$0dot5x"
      borderTopColor="$borderPrimary"
      backgroundColor="$backgroundPrimary"
    >
      <List display="flex" justifyContent="space-evenly" width="100%">
        {tabs.map((tab) => (
          <ListItem key={tab.name}>
            <NavTab
              name={tab.name}
              icon={tab.icon}
              label={tab.displayName}
              href={tab.href}
              isActive={activeTabName === tab.name}
              aria-label={tab.displayName}
              {...(isOpendoorAppEvoWebView && {
                hoverStyle: { backgroundColor: '$transparent' },
              })}
            />
          </ListItem>
        ))}
      </List>
    </Col>
  );
};

/** Desktop left-rail navigation bar. */
export const DesktopPrimaryNavBar: FC = () => {
  const activeTabName = useContext(ActiveTabNameContext);
  const tabs = useTabs();

  const { logout } = useAuthAuthenticated();

  return (
    <Col
      tag="nav"
      aria-label="Global navigation"
      height="100dvh"
      top={0}
      $platform-web={{ position: 'sticky', transition: 'all 0.3s ease' }}
      backgroundColor="$backgroundPrimary"
      data-testid="desktop-primary-nav"
      pl="$12x"
      $largerThanSM={{ width: 107 }}
      $largerThanMD={{ width: 200 }}
    >
      <Col flex={1}>
        <OpendoorNavLogo />

        <Col tag="nav" aria-label="App" justifyContent="flex-start">
          <List>
            {tabs.map((tab) => (
              <ListItem key={tab.name}>
                <NavTab
                  name={tab.name}
                  label={tab.displayName}
                  href={tab.href}
                  isActive={activeTabName === tab.name}
                  aria-label={tab.displayName}
                />
              </ListItem>
            ))}
          </List>
        </Col>
      </Col>
      <Col tag="nav" aria-label="App" justifyContent="flex-start" my="$20x">
        <List
          display="flex"
          flexDirection="column"
          gap="$10x"
          pl="$2x"
          $largerThanMD={{ pl: '$8x' }}
          $platform-web={{ transition: 'all 0.3s ease' }}
        >
          <Link
            variant="tertiary"
            size="small"
            analyticsName="cake-new-account-page"
            href={ACCOUNT_URL}
          >
            Account
          </Link>

          <TextButton
            label="Sign out"
            fontWeight="regular"
            variant="tertiary"
            size="xsmall"
            analyticsName="cake-sign-out-button"
            onPress={() => void logout()}
          />
        </List>
      </Col>
    </Col>
  );
};

const OpendoorNavLogo = () => {
  const { smallerThanMD } = useMedia();

  return (
    <Row
      alignItems="center"
      justifyContent="flex-start"
      pt="$20x"
      pb="$24x"
      pl="$2x"
      $largerThanMD={{ pl: '$8x' }}
      $platform-web={{ transition: 'all 0.3s ease' }}
    >
      <LogoLink
        variant="secondary"
        width={smallerThanMD ? '$20x' : '$24x'}
        height={smallerThanMD ? '$28x' : '$33x'}
        color="$contentPrimary"
      />
    </Row>
  );
};

interface PrimaryNavBarTab {
  /** Name used for metrics only. */
  name: TabName;
  icon: (color: NavTabColor) => ReactNode;
  displayName: string;
  href: string;
}

type UseTabsParams = {
  showMobileMore?: boolean;
};

const useTabs = (params?: UseTabsParams): PrimaryNavBarTab[] => {
  const { showMobileMore } = params ?? {};

  return useMemo(() => {
    const tabs: (PrimaryNavBarTab | undefined)[] = [
      {
        name: SELLING_TAB_NAME,
        icon: (color) => <Icons.Sign size={24} color={color} aria-hidden />,
        displayName: 'Sell',
        href: getHubSellingRoute(),
      },
      {
        name: CONTACT_TAB_NAME,
        icon: (color) => (
          <IconMessage size={24} color={getTokenValue(color as 'auto', 'color')} aria-hidden />
        ),
        displayName: 'Contact',
        href: getHubContactRoute(),
      },
      showMobileMore
        ? {
            name: MORE_TAB_NAME,
            icon: (color) => (
              <IconUser size={24} color={getTokenValue(color as 'auto', 'color')} aria-hidden />
            ),
            displayName: 'More',
            href: getHubMoreRoute(),
          }
        : undefined,
    ];

    return tabs.filter((t): t is PrimaryNavBarTab => !!t);
  }, [showMobileMore]);
};
